<template>
  <div class="invoice-page">
    <div class="page-head">
      <div>
        <h2>发票申请</h2>
        <p>基于已支付的会员会费记录提交发票申请，开票后可查看票据图片或二维码。</p>
      </div>
      <el-button type="primary" @click="openApply()">申请发票</el-button>
    </div>

    <el-tabs v-model="activeTab">
      <el-tab-pane label="会费支付记录" name="payments">
        <el-table v-loading="paymentLoading" :data="paymentRows" :height="tableHeight" border>
          <el-table-column prop="order_no" label="订单号" min-width="180" show-overflow-tooltip />
          <el-table-column prop="amount" label="金额" width="100">
            <template #default="scope">￥{{ scope.row.amount }}</template>
          </el-table-column>
          <el-table-column prop="year" label="年份" width="90" />
          <el-table-column prop="pay_status" label="支付状态" width="110">
            <template #default="scope">
              <el-tag :type="scope.row.pay_status === 1 ? 'success' : 'info'">{{ payStatusText(scope.row.pay_status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="pay_time" label="支付时间" width="170">
            <template #default="scope">{{ formatTime(scope.row.pay_time) }}</template>
          </el-table-column>
          <el-table-column prop="invoice_status_text" label="开票状态" width="110">
            <template #default="scope">
              <el-tag :type="invoiceStatusTag(scope.row.invoice_status)">{{ scope.row.invoice_status_text || '未申请' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="150" :fixed="isMobile ? false : 'right'">
            <template #default="scope">
              <el-button v-if="scope.row.pay_status === 1 && !scope.row.invoice_id" type="primary" link @click="openApply(scope.row)">申请</el-button>
              <el-button v-if="scope.row.invoice_id" type="primary" link @click="openDetail(scope.row.invoice_id)">详情</el-button>
              <span v-if="scope.row.pay_status !== 1 && !scope.row.invoice_id" class="muted">-</span>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          v-model:current-page="paymentPage"
          :page-size="paymentPageSize"
          :total="paymentTotal"
          background
          layout="total, prev, pager, next"
          class="pager"
          @current-change="loadPayments"
        />
      </el-tab-pane>

      <el-tab-pane label="开票申请记录" name="invoices">
        <el-table v-loading="invoiceLoading" :data="invoiceRows" :height="tableHeight" border>
          <el-table-column prop="order_no" label="订单号" min-width="180" show-overflow-tooltip />
          <el-table-column prop="invoice_title" label="发票抬头" min-width="180" show-overflow-tooltip />
          <el-table-column prop="title_type_text" label="抬头类型" width="100" />
          <el-table-column prop="invoice_amount" label="金额" width="100">
            <template #default="scope">￥{{ scope.row.invoice_amount }}</template>
          </el-table-column>
          <el-table-column prop="status_text" label="开票状态" width="110">
            <template #default="scope">
              <el-tag :type="invoiceStatusTag(scope.row.status)">{{ scope.row.status_text }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="create_time" label="提交时间" width="170">
            <template #default="scope">{{ formatTime(scope.row.create_time) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100" :fixed="isMobile ? false : 'right'">
            <template #default="scope">
              <el-button type="primary" link @click="openDetail(scope.row.id)">详情</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          v-model:current-page="invoicePage"
          :page-size="invoicePageSize"
          :total="invoiceTotal"
          background
          layout="total, prev, pager, next"
          class="pager"
          @current-change="loadInvoices"
        />
      </el-tab-pane>
    </el-tabs>

    <el-dialog
      v-model="applyVisible"
      title="提交发票申请"
      :width="applyDialogWidth"
      :top="isMobile ? '0' : '4vh'"
      :fullscreen="isMobile"
      append-to-body
      custom-class="invoice-apply-dialog"
      :close-on-click-modal="false"
    >
      <el-form ref="applyFormRef" :model="applyForm" :rules="rules" :label-width="formLabelWidth">
        <el-form-item label="支付订单" prop="payment_id">
          <el-select v-model="applyForm.payment_id" placeholder="请选择已支付且未申请发票的订单" filterable class="full">
            <el-option
              v-for="item in payableRows"
              :key="item.id"
              :label="`${item.order_no} / ￥${item.amount}`"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="抬头类型" prop="title_type">
          <el-radio-group v-model="applyForm.title_type" @change="handleTitleTypeChange">
            <el-radio :label="1">个人</el-radio>
            <el-radio :label="2">企业</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="常用抬头">
          <el-select
            v-model="applyForm.invoice_title_id"
            placeholder="选择常用抬头，或直接填写新抬头"
            clearable
            filterable
            class="full"
            @change="selectInvoiceTitle"
          >
            <el-option
              v-for="item in titleRows"
              :key="item.id"
              :label="titleOptionLabel(item)"
              :value="item.id"
            />
          </el-select>
          <el-button class="reuse-btn" type="primary" link @click="loadReusableTitle">使用默认/最近抬头</el-button>
        </el-form-item>
        <el-form-item label="发票抬头" prop="invoice_title">
          <el-input v-model="applyForm.invoice_title" placeholder="个人姓名或企业名称" />
        </el-form-item>
        <template v-if="applyForm.title_type === 2">
          <el-form-item label="纳税人识别号" prop="tax_no">
            <el-input v-model="applyForm.tax_no" placeholder="统一社会信用代码/税号" />
          </el-form-item>
          <el-form-item label="企业地址">
            <el-input v-model="applyForm.company_address" placeholder="选填" />
          </el-form-item>
          <el-form-item label="企业电话">
            <el-input v-model="applyForm.company_phone" placeholder="选填" />
          </el-form-item>
          <el-form-item label="开户银行">
            <el-input v-model="applyForm.bank_name" placeholder="选填" />
          </el-form-item>
          <el-form-item label="银行账号">
            <el-input v-model="applyForm.bank_account" placeholder="选填" />
          </el-form-item>
        </template>
        <el-form-item label="收票联系人" prop="contact_name">
          <el-input v-model="applyForm.contact_name" />
        </el-form-item>
        <el-form-item label="收票手机号" prop="contact_phone">
          <el-input v-model="applyForm.contact_phone" />
        </el-form-item>
        <el-form-item label="收票邮箱" prop="contact_email">
          <el-input v-model="applyForm.contact_email" placeholder="用于接收电子票据" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="applyForm.remark" type="textarea" :rows="3" placeholder="选填" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="applyVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitApply">提交申请</el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="detailVisible"
      title="发票详情"
      :width="detailDialogWidth"
      :top="isMobile ? '0' : '5vh'"
      :fullscreen="isMobile"
      append-to-body
      custom-class="invoice-detail-dialog"
    >
      <el-descriptions :column="detailColumns" border>
        <el-descriptions-item label="订单号">{{ detail.order_no }}</el-descriptions-item>
        <el-descriptions-item label="金额">￥{{ detail.invoice_amount }}</el-descriptions-item>
        <el-descriptions-item label="抬头类型">{{ detail.title_type_text }}</el-descriptions-item>
        <el-descriptions-item label="开票状态">
          <el-tag :type="invoiceStatusTag(detail.status)">{{ detail.status_text }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="发票抬头" :span="2">{{ detail.invoice_title }}</el-descriptions-item>
        <el-descriptions-item v-if="detail.title_type === 2" label="纳税人识别号" :span="2">{{ detail.tax_no }}</el-descriptions-item>
        <el-descriptions-item label="收票联系人">{{ detail.contact_name }}</el-descriptions-item>
        <el-descriptions-item label="收票手机号">{{ detail.contact_phone }}</el-descriptions-item>
        <el-descriptions-item label="收票邮箱" :span="2">{{ detail.contact_email || '-' }}</el-descriptions-item>
        <el-descriptions-item label="备注" :span="2">{{ detail.remark || '-' }}</el-descriptions-item>
        <el-descriptions-item label="后台备注" :span="2">{{ detail.admin_remark || '-' }}</el-descriptions-item>
      </el-descriptions>
      <div class="ticket-assets" v-if="detail.invoice_file || detail.invoice_qrcode">
        <el-image v-if="detail.invoice_file" :src="detail.invoice_file" :preview-src-list="[detail.invoice_file]" fit="cover" />
        <el-image v-if="detail.invoice_qrcode" :src="detail.invoice_qrcode" :preview-src-list="[detail.invoice_qrcode]" fit="cover" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/api/index'
import invoice from '@/api/invoice'
import dayjs from 'dayjs'

export default {
  name: 'MemberInvoice',
  data() {
    return {
      activeTab: 'payments',
      paymentLoading: false,
      invoiceLoading: false,
      paymentRows: [],
      invoiceRows: [],
      titleRows: [],
      paymentPage: 1,
      paymentPageSize: 10,
      paymentTotal: 0,
      invoicePage: 1,
      invoicePageSize: 10,
      invoiceTotal: 0,
      applyVisible: false,
      submitting: false,
      detailVisible: false,
      detail: {},
      windowWidth: window.innerWidth,
      applyForm: this.defaultForm(),
      rules: {
        payment_id: [{ required: true, message: '请选择支付订单', trigger: 'change' }],
        title_type: [{ required: true, message: '请选择抬头类型', trigger: 'change' }],
        invoice_title: [{ required: true, message: '请填写发票抬头', trigger: 'blur' }],
        tax_no: [{ required: true, message: '请填写纳税人识别号', trigger: 'blur' }],
        contact_name: [{ required: true, message: '请填写收票联系人', trigger: 'blur' }],
        contact_phone: [{ required: true, message: '请填写收票手机号', trigger: 'blur' }],
        contact_email: [
          { required: true, message: '请填写收票邮箱', trigger: 'blur' },
          { type: 'email', message: '请填写正确的邮箱', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth <= 768
    },
    applyDialogWidth() {
      if (this.windowWidth <= 480) return '100%'
      if (this.windowWidth <= 900) return '92%'
      return '720px'
    },
    detailDialogWidth() {
      if (this.windowWidth <= 480) return '100%'
      if (this.windowWidth <= 900) return '92%'
      return '760px'
    },
    formLabelWidth() {
      return this.isMobile ? '92px' : '120px'
    },
    detailColumns() {
      return this.isMobile ? 1 : 2
    },
    tableHeight() {
      return this.isMobile ? 420 : undefined
    },
    payableRows() {
      return this.paymentRows.filter(item => item.pay_status === 1 && !item.invoice_id)
    }
  },
  created() {
    this.loadPayments()
    this.loadInvoices()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    defaultForm() {
      return {
        payment_id: null,
        invoice_title_id: null,
        title_type: 1,
        invoice_title: '',
        tax_no: '',
        company_address: '',
        company_phone: '',
        bank_name: '',
        bank_account: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        remark: ''
      }
    },
    async loadPayments() {
      this.paymentLoading = true
      try {
        const res = await api.paymentList({
          page: this.paymentPage,
          limit: this.paymentPageSize
        })
        if (res && res.data) {
          this.paymentRows = res.data.data || []
          this.paymentTotal = res.data.total || 0
        }
      } finally {
        this.paymentLoading = false
      }
    },
    async loadInvoices() {
      this.invoiceLoading = true
      try {
        const res = await invoice.getList({
          page: this.invoicePage,
          limit: this.invoicePageSize
        })
        if (res && res.data) {
          this.invoiceRows = res.data.data || []
          this.invoiceTotal = res.data.total || 0
        }
      } finally {
        this.invoiceLoading = false
      }
    },
    async loadTitleList() {
      // 申请发票时从常用抬头表读取候选项，而不是从历史申请里直接截取。
      const res = await invoice.titleList({ title_type: this.applyForm.title_type })
      this.titleRows = res && res.data ? res.data : []
    },
    async openApply(row) {
      this.applyForm = this.defaultForm()
      if (row && row.id) {
        this.applyForm.payment_id = row.id
      }
      this.applyVisible = true
      await this.loadTitleList()
      await this.loadReusableTitle()
      this.$nextTick(() => {
        this.$refs.applyFormRef && this.$refs.applyFormRef.clearValidate()
      })
    },
    async handleTitleTypeChange() {
      const paymentId = this.applyForm.payment_id
      this.applyForm = {
        ...this.defaultForm(),
        payment_id: paymentId,
        title_type: this.applyForm.title_type
      }
      await this.loadTitleList()
      await this.loadReusableTitle()
    },
    async loadReusableTitle() {
      // 默认/最近抬头用于快速回填；用户仍可继续修改，提交时保存为本次申请快照。
      const res = await invoice.reusableTitle({ title_type: this.applyForm.title_type })
      if (res && res.data) {
        this.fillTitle(res.data)
      }
    },
    selectInvoiceTitle(id) {
      const row = this.titleRows.find(item => item.id === id)
      if (row) {
        this.fillTitle(row)
      }
    },
    fillTitle(data) {
      // 只把抬头资料写入申请表单，支付订单和用户备注保持当前上下文。
      Object.assign(this.applyForm, {
        invoice_title_id: data.id || null,
        title_type: data.title_type || this.applyForm.title_type,
        invoice_title: data.invoice_title || '',
        tax_no: data.tax_no || '',
        company_address: data.company_address || '',
        company_phone: data.company_phone || '',
        bank_name: data.bank_name || '',
        bank_account: data.bank_account || '',
        contact_name: data.contact_name || '',
        contact_phone: data.contact_phone || '',
        contact_email: data.contact_email || ''
      })
    },
    titleOptionLabel(item) {
      const suffix = item.title_type === 2 && item.tax_no ? ` / ${item.tax_no}` : ''
      return `${item.invoice_title}${suffix}${item.is_default ? ' / 默认' : ''}`
    },
    submitApply() {
      this.$refs.applyFormRef.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          const res = await invoice.submit(this.applyForm)
          if (res) {
            this.$message.success(res.msg || '提交成功')
            this.applyVisible = false
            await this.loadPayments()
            await this.loadInvoices()
            this.activeTab = 'invoices'
          }
        } finally {
          this.submitting = false
        }
      })
    },
    async openDetail(id) {
      const res = await invoice.getDetail({ id })
      if (res && res.data) {
        this.detail = res.data
        this.detailVisible = true
      }
    },
    payStatusText(status) {
      const map = { 0: '待支付', 1: '已支付', 2: '支付失败', 3: '已关闭', 4: '已退款' }
      return map[status] || '-'
    },
    invoiceStatusTag(status) {
      const map = { 0: 'warning', 1: 'success', 2: 'danger' }
      return status === null || status === undefined ? 'info' : (map[status] || 'info')
    },
    formatTime(value) {
      if (!value) return '-'
      if (typeof value === 'string' && value.includes('-')) return value
      const n = Number(value)
      if (!n) return '-'
      return n > 1e12 ? dayjs(n).format('YYYY-MM-DD HH:mm:ss') : dayjs.unix(n).format('YYYY-MM-DD HH:mm:ss')
    }
  }
}
</script>

<style scoped>
.invoice-page {
  padding: 24px;
}

.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.page-head h2 {
  margin: 0 0 8px;
  font-size: 22px;
  color: #303133;
}

.page-head p {
  margin: 0;
  color: #606266;
  line-height: 1.6;
}

.pager {
  margin-top: 20px;
  justify-content: flex-end;
}

.full {
  width: 100%;
}

.reuse-btn {
  margin-left: 12px;
}

.muted {
  color: #909399;
}

.ticket-assets {
  display: flex;
  gap: 16px;
  margin-top: 18px;
}

.ticket-assets .el-image {
  width: 160px;
  height: 160px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .invoice-page {
    padding: 16px;
  }

  .page-head {
    flex-direction: column;
  }

  .page-head .el-button {
    width: 100%;
  }

  .pager {
    justify-content: center;
  }

  .reuse-btn {
    display: block;
    margin-left: 0;
    margin-top: 6px;
    width: fit-content;
  }
}
</style>

<style>
.invoice-apply-dialog,
.invoice-detail-dialog {
  max-width: calc(100vw - 32px);
}

.invoice-apply-dialog .el-dialog__body,
.invoice-detail-dialog .el-dialog__body {
  max-height: calc(92vh - 126px);
  overflow-y: auto;
  padding-right: 24px;
}

.invoice-apply-dialog .el-dialog__footer,
.invoice-detail-dialog .el-dialog__footer {
  border-top: 1px solid #ebeef5;
  padding-top: 16px;
}

.invoice-apply-dialog .el-select,
.invoice-apply-dialog .el-input,
.invoice-apply-dialog .el-textarea {
  width: 100%;
}

@media (max-width: 768px) {
  .invoice-apply-dialog,
  .invoice-detail-dialog {
    max-width: 100vw;
  }

  .invoice-apply-dialog .el-dialog__header,
  .invoice-detail-dialog .el-dialog__header {
    padding: 16px 16px 10px;
  }

  .invoice-apply-dialog .el-dialog__body,
  .invoice-detail-dialog .el-dialog__body {
    max-height: calc(100vh - 122px);
    padding: 12px 16px;
  }

  .invoice-apply-dialog .el-dialog__footer,
  .invoice-detail-dialog .el-dialog__footer {
    padding: 12px 16px 16px;
  }

  .invoice-apply-dialog .el-form-item {
    margin-bottom: 16px;
  }

  .invoice-apply-dialog .el-dialog__footer .el-button {
    min-width: 96px;
  }
}
</style>
